import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Upload, GripVertical, Plus } from "lucide-react";
import { api, API, ASSET_BASE, resolveImg } from "../../lib/api";
import { auth } from "../../lib/auth";

const EMPTY = {
  slug: "", title: "", category: "musica", location: "", year: "",
  cover: "", images: [], description: "", intro_quote: "", essay: "",
  client: "", artists: [], credits: "", behance_url: "",
  featured: false, order: 0,
};

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
   .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminProjectEditor() {
  const { slug } = useParams();
  const isNew = !slug || slug === "new";
  const navigate = useNavigate();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isNew) return;
    api.get(`/projects/${slug}`).then((r) => {
      setData({ ...EMPTY, ...r.data });
      setLoading(false);
    }).catch(() => { setErr("No se pudo cargar"); setLoading(false); });
  }, [slug, isNew]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    // Client-side validation
    const titleClean = (data.title || "").trim();
    const slugClean = ((data.slug || slugify(data.title)) || "").trim();
    if (!titleClean) { setErr("El título es obligatorio"); setSaving(false); return; }
    if (!slugClean) { setErr("El slug es obligatorio"); setSaving(false); return; }
    try {
      if (isNew) {
        const cover = data.cover || (data.images[0]?.url ?? "");
        const r = await api.post("/admin/projects", { ...data, slug: slugClean, title: titleClean, cover });
        setMsg("Proyecto creado");
        navigate(`/admin/projects/${r.data.slug}`, { replace: true });
      } else {
        const cover = data.cover || (data.images[0]?.url ?? "");
        await api.put(`/admin/projects/${slug}`, { ...data, slug: slugClean, title: titleClean, cover });
        setMsg("Cambios guardados");
        if (slugClean !== slug) navigate(`/admin/projects/${slugClean}`, { replace: true });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Error al guardar");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 2400);
    }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar este proyecto?")) return;
    await api.delete(`/admin/projects/${slug}`);
    navigate("/admin", { replace: true });
  };

  const addImages = (newImages) => {
    setData((d) => {
      const nextImages = [...d.images, ...newImages];
      // Auto-set cover if it's empty OR if it still points to a Behance URL (legacy seed)
      const isLegacyCover = !d.cover || d.cover.includes("behance.net") || d.cover.includes("mir-s3-cdn-cf.behance");
      const nextCover = isLegacyCover && newImages[0]?.url ? newImages[0].url : d.cover;
      return { ...d, images: nextImages, cover: nextCover };
    });
  };
  const setAsCover = (i) => {
    setData((d) => ({ ...d, cover: d.images[i]?.url || d.cover }));
  };
  const updateImage = (i, k, v) => {
    setData((d) => ({ ...d, images: d.images.map((img, idx) => (idx === i ? { ...img, [k]: v } : img)) }));
  };
  const removeImage = (i) => {
    setData((d) => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
  };
  const moveImage = (from, to) => {
    setData((d) => {
      const arr = [...d.images];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return { ...d, images: arr };
    });
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white/40 font-mono text-xs p-12 tracking-widest">CARGANDO…</div>;
  }

  return (
    <div data-testid="admin-editor" className="min-h-screen bg-black text-white pb-24">
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-12 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-display text-white/60 hover:text-white">
            <ArrowLeft size={14} /> Volver
          </Link>
          <div className="flex items-center gap-3">
            {msg && <span className="text-[var(--cyan)] text-[11px] tracking-[0.2em] uppercase font-display">{msg}</span>}
            {err && <span className="text-[var(--magenta)] text-[11px] tracking-[0.2em] uppercase font-display">{err}</span>}
            {!isNew && (
              <button onClick={remove}
                className="inline-flex items-center gap-2 border border-white/10 hover:border-[var(--magenta)] hover:text-[var(--magenta)] px-4 py-2 text-[11px] tracking-[0.3em] uppercase font-display">
                <Trash2 size={12} /> Eliminar
              </button>
            )}
            <button onClick={save} disabled={saving} data-testid="admin-save"
              className="inline-flex items-center gap-2 border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white disabled:opacity-50 px-5 py-2 text-[11px] tracking-[0.3em] uppercase font-display transition">
              <Save size={14} /> {saving ? "Guardando…" : isNew ? "Crear" : "Guardar"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 sm:px-12 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <section className="lg:col-span-2 space-y-8">
          {/* BÁSICOS */}
          <Block title="01 · Información básica">
            <Field label="Título">
              <input value={data.title} onChange={(e) => update("title", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-3 text-2xl font-display uppercase tracking-tight" />
            </Field>
            <Field label="Slug (URL)" hint="solo minúsculas, números y guiones">
              <input value={data.slug} onChange={(e) => update("slug", slugify(e.target.value))}
                placeholder={data.title ? slugify(data.title) : ""}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2 font-mono text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Field label="Categoría">
                <select value={data.category} onChange={(e) => update("category", e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2 text-white">
                  <option value="musica" className="bg-black">Música</option>
                  <option value="eventos" className="bg-black">Eventos</option>
                  <option value="viajes" className="bg-black">Viajes</option>
                  <option value="retratos" className="bg-black">Retratos</option>
                </select>
              </Field>
              <Field label="Ubicación">
                <input value={data.location || ""} onChange={(e) => update("location", e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2" />
              </Field>
              <Field label="Año">
                <input value={data.year || ""} onChange={(e) => update("year", e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2" />
              </Field>
            </div>
          </Block>

          {/* TEXTOS NARRATIVOS */}
          <Block title="02 · Narrativa">
            <Field label="Cita / pull-quote (1-2 líneas)">
              <input value={data.intro_quote || ""} onChange={(e) => update("intro_quote", e.target.value)}
                placeholder="Una frase fuerte que abra el proyecto"
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2 text-lg font-light" />
            </Field>
            <Field label="Descripción corta (1 párrafo)">
              <textarea rows={3} value={data.description || ""} onChange={(e) => update("description", e.target.value)}
                className="w-full bg-transparent border border-white/15 focus:border-[var(--magenta)] outline-none p-3 text-sm leading-relaxed resize-y" />
            </Field>
            <Field label="Ensayo / texto largo (opcional)" hint="Markdown ligero — separa párrafos con líneas en blanco">
              <textarea rows={10} value={data.essay || ""} onChange={(e) => update("essay", e.target.value)}
                placeholder="Cuenta la historia. Contexto, anécdotas, lo que viste, por qué importa."
                className="w-full bg-transparent border border-white/15 focus:border-[var(--magenta)] outline-none p-3 text-sm leading-relaxed resize-y" />
            </Field>
          </Block>

          {/* IMÁGENES */}
          <Block title={`03 · Imágenes (${data.images.length})`}>
            <ImageUploader onUploaded={addImages} />
            {data.images.length === 0 && <p className="text-white/40 text-sm py-6">Sin imágenes todavía. Arrastra archivos arriba.</p>}
            <ul className="space-y-3 mt-6">
              {data.images.map((img, i) => {
                const isCover = data.cover === img.url;
                return (
                <li key={img.url || `img-${i}`} className={`flex gap-4 border p-3 ${isCover ? "border-[var(--magenta)]" : "border-white/10"}`}>
                  <div className="flex flex-col items-center gap-2 self-stretch">
                    <button title="Subir" onClick={() => i > 0 && moveImage(i, i - 1)} className="text-white/40 hover:text-white">↑</button>
                    <span className="font-mono text-[10px] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                    <button title="Bajar" onClick={() => i < data.images.length - 1 && moveImage(i, i + 1)} className="text-white/40 hover:text-white">↓</button>
                  </div>
                  <div className="relative">
                    <img src={resolveImg(img.url)} alt="" className="w-32 h-24 object-cover bg-[var(--surface)]" />
                    {isCover && (
                      <span className="absolute top-1 left-1 bg-[var(--magenta)] text-white font-mono text-[8px] tracking-[0.3em] px-1.5 py-0.5">PORTADA</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input value={img.caption || ""} onChange={(e) => updateImage(i, "caption", e.target.value)}
                      placeholder="Pie de foto (opcional)"
                      className="w-full bg-transparent border-b border-white/15 focus:border-[var(--magenta)] outline-none py-1.5 text-sm" />
                    <input value={img.alt || ""} onChange={(e) => updateImage(i, "alt", e.target.value)}
                      placeholder="ALT (descripción para accesibilidad / SEO)"
                      className="w-full bg-transparent border-b border-white/15 focus:border-[var(--magenta)] outline-none py-1.5 text-sm" />
                    <input value={img.technical || ""} onChange={(e) => updateImage(i, "technical", e.target.value)}
                      placeholder="Datos técnicos (cámara · lente · ISO)"
                      className="w-full bg-transparent border-b border-white/15 focus:border-[var(--magenta)] outline-none py-1.5 text-sm font-mono" />
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setAsCover(i)}
                        disabled={isCover}
                        data-testid={`set-cover-${i}`}
                        className={`text-[10px] tracking-[0.3em] uppercase font-display border px-3 py-1.5 transition ${
                          isCover
                            ? "border-[var(--magenta)] text-[var(--magenta)] cursor-default"
                            : "border-white/20 text-white/60 hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                        }`}
                      >
                        {isCover ? "✓ portada" : "Usar como portada"}
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeImage(i)} className="self-start text-white/30 hover:text-[var(--magenta)] p-1">
                    <Trash2 size={14} />
                  </button>
                </li>
                );
              })}
            </ul>
          </Block>

          {/* CRÉDITOS */}
          <Block title="04 · Créditos">
            <Field label="Cliente">
              <input value={data.client || ""} onChange={(e) => update("client", e.target.value)}
                placeholder="Festival X · Sello Y · Banda Z"
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2" />
            </Field>
            <Field label="Artistas / personas (separadas por coma)">
              <input
                value={(data.artists || []).join(", ")}
                onChange={(e) => update("artists", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="Stephy Farah, Nella, …"
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2" />
            </Field>
            <Field label="Créditos detallados (multilínea)">
              <textarea rows={4} value={data.credits || ""} onChange={(e) => update("credits", e.target.value)}
                placeholder="Producción · Dirección · Equipo · Locación …"
                className="w-full bg-transparent border border-white/15 focus:border-[var(--magenta)] outline-none p-3 text-sm font-mono resize-y" />
            </Field>
            <Field label="URL de Behance (opcional)">
              <input value={data.behance_url || ""} onChange={(e) => update("behance_url", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2 font-mono text-sm" />
            </Field>
          </Block>
        </section>

        {/* SIDEBAR */}
        <aside className="space-y-6">
          <div className="border border-white/10 p-5 space-y-4">
            <p className="font-mono text-[10px] tracking-[0.4em] text-white/40">META</p>
            <Field label="Portada (URL)" hint="se autollena con la 1ª imagen">
              <input value={data.cover || ""} onChange={(e) => update("cover", e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2 font-mono text-xs" />
            </Field>
            {data.cover && (
              <CoverFocusPicker
                src={resolveImg(data.cover)}
                x={data.cover_focus_x ?? 50}
                y={data.cover_focus_y ?? 50}
                onChange={(nx, ny) => setData((d) => ({ ...d, cover_focus_x: nx, cover_focus_y: ny }))}
              />
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!data.featured} onChange={(e) => update("featured", e.target.checked)} />
              <span className="text-sm">Destacar en la home</span>
            </label>
            <Field label="Orden (menor = primero)">
              <input type="number" value={data.order ?? 0} onChange={(e) => update("order", parseInt(e.target.value, 10) || 0)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--magenta)] outline-none py-2" />
            </Field>
            {!isNew && (
              <Link to={`/proyecto/${data.slug}`} target="_blank" rel="noreferrer"
                className="block text-center border border-white/10 hover:border-white px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-display">
                Ver en sitio →
              </Link>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="border border-white/10 p-6 space-y-5">
      <p className="font-mono text-[10px] tracking-[0.4em] text-[var(--magenta)]">{title}</p>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.4em] uppercase font-display text-white/40">{label}</span>
      {hint && <span className="block text-[10px] text-white/30 font-mono mt-0.5">{hint}</span>}
      {children}
    </label>
  );
}

function ImageUploader({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const handle = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`${i + 1}/${files.length}`);
      const fd = new FormData();
      fd.append("file", f);
      const t = auth.token();
      try {
        const r = await fetch(`${API}/admin/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${t}` },
          body: fd,
        });
        if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
        const d = await r.json();
        uploaded.push({ url: d.url, caption: "", alt: f.name.replace(/\.[^.]+$/, ""), technical: "" });
      } catch (e) {
        console.error("upload error", e);
      }
    }
    setBusy(false);
    setProgress(null);
    if (uploaded.length) onUploaded(uploaded);
  };

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files); }}
      className="border border-dashed border-white/20 hover:border-[var(--magenta)] p-8 text-center cursor-pointer transition"
      onClick={() => inputRef.current?.click()}
      data-testid="image-dropzone"
    >
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => handle(e.target.files)} />
      <Upload size={20} className="mx-auto text-white/40 mb-3" />
      <p className="font-display uppercase tracking-[0.2em] text-sm">
        {busy ? `Subiendo ${progress}…` : "Arrastra imágenes aquí"}
      </p>
      <p className="font-mono text-[10px] text-white/40 mt-2">JPEG · PNG · WEBP · varias a la vez</p>
    </div>
  );
}

/* ─────────── Cover focus picker ───────────
   Click or drag on the preview to pick the focal point of the cover photo.
   Stores the chosen point as cover_focus_x/y (0-100, percent), which is
   applied site-wide via `object-position: {x}% {y}%` on every <img> that
   uses this cover (Home desktop+mobile, ProjectDetail hero, Gallery cards). */
function CoverFocusPicker({ src, x, y, onChange }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleEvent = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const nx = ((point.clientX - rect.left) / rect.width) * 100;
    const ny = ((point.clientY - rect.top) / rect.height) * 100;
    onChange(Math.max(0, Math.min(100, nx)), Math.max(0, Math.min(100, ny)));
  };

  const onDown = (e) => { setDragging(true); handleEvent(e); };
  const onMove = (e) => { if (dragging) { e.preventDefault(); handleEvent(e); } };
  const onUp = () => setDragging(false);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div className="space-y-2" data-testid="cover-focus-picker">
      <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
        Reencuadre · clic para fijar el punto focal
      </p>

      {/* The PREVIEW shows the cover at the same 3:2 aspect used on Home cards,
          with the live object-position applied so you see exactly what visitors will. */}
      <div
        ref={ref}
        onMouseDown={onDown}
        onTouchStart={onDown}
        className="relative w-full aspect-[3/2] overflow-hidden bg-[var(--surface)] cursor-crosshair select-none touch-none"
      >
        <img
          src={src}
          alt="Reencuadre de portada"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: `${x}% ${y}%` }}
        />
        {/* rule-of-thirds grid for visual guidance */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 inset-x-0 border-t border-white/15" />
          <div className="absolute top-2/3 inset-x-0 border-t border-white/15" />
          <div className="absolute left-1/3 inset-y-0 border-l border-white/15" />
          <div className="absolute left-2/3 inset-y-0 border-l border-white/15" />
        </div>
        {/* crosshair at chosen focal point */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${x}%`, top: `${y}%`,
            transform: "translate(-50%, -50%)",
            width: 28, height: 28,
          }}
        >
          <div className="absolute inset-0 rounded-full border-2 border-[var(--magenta)] shadow-[0_0_18px_rgba(236,30,140,0.7)]" />
          <div className="absolute left-1/2 top-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--magenta)]" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 font-mono text-[10px] tracking-[0.2em] text-white/50">
        <label className="space-y-1">
          <span>Foco X · {Math.round(x)}%</span>
          <input
            type="range" min="0" max="100" step="1" value={x}
            onChange={(e) => onChange(parseFloat(e.target.value), y)}
            className="w-full accent-[var(--magenta)]"
          />
        </label>
        <label className="space-y-1">
          <span>Foco Y · {Math.round(y)}%</span>
          <input
            type="range" min="0" max="100" step="1" value={y}
            onChange={(e) => onChange(x, parseFloat(e.target.value))}
            className="w-full accent-[var(--magenta)]"
          />
        </label>
      </div>
      <button
        type="button"
        onClick={() => onChange(50, 50)}
        className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-[var(--magenta)] transition"
        data-testid="cover-focus-reset"
      >
        ↺ Centrar
      </button>
    </div>
  );
}
